/**
 * Dynamic Header Color
 * Automatically adjusts header text/icon colors based on the section behind it
 * Sections should have data-header-contrast="light" or data-header-contrast="dark"
 */
class DynamicHeaderColor {
  constructor() {
    this.header = document.querySelector('.header-wrapper');
    if (!this.header) return;
    
    this.currentContrast = null;
    this.init();
  }
  
  init() {
    // Create observer to watch sections
    const observerOptions = {
      root: null,
      rootMargin: '-120px 0px -50% 0px', // Trigger when section enters top portion
      threshold: [0, 0.1, 0.5, 1]
    };
    
    this.observer = new IntersectionObserver((entries) => {
      // Find the section that's most visible at the top
      let topSection = null;
      let topPosition = Infinity;
      
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const rect = entry.boundingClientRect;
          if (rect.top < topPosition && rect.top >= -150) {
            topPosition = rect.top;
            topSection = entry.target;
          }
        }
      });
      
      if (topSection) {
        const contrast = topSection.dataset.headerContrast || 'light';
        this.updateHeaderColors(contrast);
      }
    }, observerOptions);
    
    // Observe all sections with data-header-contrast
    const sections = document.querySelectorAll('[data-header-contrast]');
    sections.forEach(section => {
      this.observer.observe(section);
    });
    
    // If no sections found, still check initial state
    if (sections.length === 0) {
      // Wait a bit for dynamic content to load
      setTimeout(() => {
        const delayedSections = document.querySelectorAll('[data-header-contrast]');
        delayedSections.forEach(section => {
          this.observer.observe(section);
        });
        this.checkInitialSection();
      }, 500);
    }
    
    // Check initial section on load
    this.checkInitialSection();
    
    // Also check on scroll for better accuracy
    let scrollTimeout;
    const checkScrollState = () => {
      if (this.header.classList.contains('scrolled-past-header')) {
        // When scrolled, always set to light (black text)
        this.updateHeaderColors('light');
      } else {
        // When not scrolled, check sections or default to light (black text)
        const sections = Array.from(document.querySelectorAll('[data-header-contrast]'));
        if (sections.length > 0) {
          this.checkCurrentSection();
        } else {
          // Default to light (black text) — hero is light-colored
          this.updateHeaderColors('light');
        }
      }
    };
    
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(checkScrollState, 50);
    }, { passive: true });
    
    // Also listen for the scrolled-past-header class changes on the header
    const observer = new MutationObserver(() => {
      checkScrollState();
    });
    observer.observe(this.header, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    // Re-check after a delay to catch any dynamically loaded sections
    setTimeout(() => {
      this.checkCurrentSection();
    }, 1000);
  }
  
  checkInitialSection() {
    const sections = Array.from(document.querySelectorAll('[data-header-contrast]'));
    
    if (sections.length === 0) {
      // Default to light (black text) regardless of scroll state
      this.updateHeaderColors('light');
      return;
    }
    
    for (const section of sections) {
      const rect = section.getBoundingClientRect();
      if (rect.top <= 150 && rect.bottom > 0) {
        const contrast = section.dataset.headerContrast || 'light';
        this.updateHeaderColors(contrast);
        return;
      }
    }
    
    // Fallback: use first section or default to light
    if (sections.length > 0) {
      const firstRect = sections[0].getBoundingClientRect();
      if (firstRect.top < 200) {
        const contrast = sections[0].dataset.headerContrast || 'light';
        this.updateHeaderColors(contrast);
      } else {
        this.updateHeaderColors('light');
      }
    } else {
      this.updateHeaderColors('light');
    }
  }
  
  checkCurrentSection() {
    const sections = Array.from(document.querySelectorAll('[data-header-contrast]'));
    let currentSection = null;
    let minDistance = Infinity;
    
    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      // Check if section is near the top (within header area)
      if (rect.top <= 150 && rect.bottom > 0) {
        const distance = Math.abs(rect.top);
        if (distance < minDistance) {
          minDistance = distance;
          currentSection = section;
        }
      }
    });
    
    if (currentSection) {
      const contrast = currentSection.dataset.headerContrast || 'light';
      this.updateHeaderColors(contrast);
    } else {
      // No section detected at top — default to light (black text)
      this.updateHeaderColors('light');
    }
  }
  
  updateHeaderColors(contrast) {
    // Don't update if already set to this contrast
    if (this.currentContrast === contrast) return;
    
    this.currentContrast = contrast;
    const isLight = contrast === 'light';
    const textColor = isLight ? '#000000' : '#ffffff';
    
    // Update data attribute for CSS targeting
    this.header.setAttribute('data-contrast', contrast);
    
    // Force update inline styles immediately as fallback
    const logo = this.header.querySelector('.header__heading-link .h2');
    if (logo) {
      logo.style.setProperty('color', textColor, 'important');
      logo.style.transition = 'color 0.3s ease';
    }
    
    const menuItems = this.header.querySelectorAll('.header__menu-item');
    menuItems.forEach(item => {
      item.style.setProperty('color', textColor, 'important');
      item.style.transition = 'color 0.3s ease';
    });
    
    const icons = this.header.querySelectorAll('.header__icon');
    icons.forEach(icon => {
      icon.style.setProperty('color', textColor, 'important');
      icon.style.transition = 'color 0.3s ease, fill 0.3s ease';
      
      const svg = icon.querySelector('.icon, svg');
      if (svg) {
        svg.style.setProperty('fill', textColor, 'important');
        svg.style.transition = 'fill 0.3s ease';
      }
      
      const paths = icon.querySelectorAll('path');
      paths.forEach(path => {
        path.style.setProperty('fill', textColor, 'important');
      });
    });
    
    // Force a reflow to ensure styles apply
    void this.header.offsetHeight;
  }
}

// Initialize immediately and also on DOM ready to catch early and late loads
(function() {
  if (window.__pheDynamicHeaderColorInit) return;
  window.__pheDynamicHeaderColorInit = true;

  const init = () => {
    if (!document.querySelector('.header-wrapper')) return;
    if (!window.__pheDynamicHeaderColorInstance) {
      window.__pheDynamicHeaderColorInstance = new DynamicHeaderColor();
    }
  };

  // Try to initialize immediately
  init();
  
  // Also initialize on DOM ready in case header loads later
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
    });
  }
  
  // Fallback: initialize after a short delay to catch any late-loading headers
  setTimeout(() => {
    init();
  }, 100);
})();